/* Simple rich text editor for blogs and pages (Visual + HTML mode) */
import React, { useEffect, useRef, useState } from "react";
import { useWA, cx, strip, num } from "./ui";
import { absUrl, prepareImage } from "./api";

const TOOLS = [
  ["undo", "fa-solid fa-rotate-left", "Undo"],
  ["redo", "fa-solid fa-rotate-right", "Redo"],
  "|",
  ["h2", "H2", "Heading 2"],
  ["h3", "H3", "Heading 3"],
  ["h4", "H4", "Heading 4"],
  ["p", "¶", "Normal text"],
  "|",
  ["bold", "fa-solid fa-bold", "Bold"],
  ["italic", "fa-solid fa-italic", "Italic"],
  ["underline", "fa-solid fa-underline", "Underline"],
  ["strikeThrough", "fa-solid fa-strikethrough", "Strike"],
  "|",
  ["insertUnorderedList", "fa-solid fa-list-ul", "Bullet list"],
  ["insertOrderedList", "fa-solid fa-list-ol", "Numbered list"],
  ["quote", "fa-solid fa-quote-left", "Quote"],
  "|",
  ["justifyLeft", "fa-solid fa-align-left", "Left"],
  ["justifyCenter", "fa-solid fa-align-center", "Center"],
  ["justifyRight", "fa-solid fa-align-right", "Right"],
  "|",
  ["link", "fa-solid fa-link", "Link"],
  ["unlink", "fa-solid fa-link-slash", "Remove link"],
  ["image", "fa-regular fa-image", "Image upload"],
  ["imageUrl", "fa-solid fa-globe", "Image from URL"],
  ["table", "fa-solid fa-table", "Table"],
  ["hr", "fa-solid fa-minus", "Line"],
  ["removeFormat", "fa-solid fa-text-slash", "Clear format"],
];

export default function RichEditor({ value, onChange, placeholder = "यहाँ लिखें…", minHeight = 420 }) {
  const { api, toast, prompt } = useWA();
  const ref = useRef(null);
  const last = useRef(null);
  const file = useRef(null);
  const saved = useRef(null);
  const [html, setHtml] = useState(false);
  const [busy, setBusy] = useState(false);

  // only push outside changes into the editor (keeps the cursor in place while typing)
  useEffect(() => {
    if (!html && ref.current && (value || "") !== ref.current.innerHTML) {
      ref.current.innerHTML = value || "";
      last.current = value;
    }
  }, [value, html]);

  const emit = () => {
    if (!ref.current) return;
    const v = ref.current.innerHTML === "<br>" ? "" : ref.current.innerHTML;
    last.current = v;
    onChange(v);
  };

  const keepSel = () => {
    const s = window.getSelection();
    if (s && s.rangeCount && ref.current && ref.current.contains(s.anchorNode)) saved.current = s.getRangeAt(0).cloneRange();
  };
  const restoreSel = () => {
    if (!saved.current) return ref.current && ref.current.focus();
    ref.current.focus();
    const s = window.getSelection();
    s.removeAllRanges();
    s.addRange(saved.current);
  };

  const exec = (cmd, arg) => {
    restoreSel();
    document.execCommand(cmd, false, arg);
    emit();
  };

  const insertHtml = (h) => exec("insertHTML", h);

  const tool = async (k) => {
    if (["h2", "h3", "h4", "p"].includes(k)) return exec("formatBlock", k.toUpperCase());
    if (k === "quote") return exec("formatBlock", "BLOCKQUOTE");
    if (k === "hr") return exec("insertHorizontalRule");
    if (k === "link") {
      keepSel();
      const url = await prompt({ title: "Link जोड़ें", placeholder: "https://…", defaultValue: "https://" });
      if (url) exec("createLink", url);
      return null;
    }
    if (k === "imageUrl") {
      keepSel();
      const url = await prompt({ title: "Image URL", placeholder: "https://…/photo.jpg" });
      if (url) insertHtml(`<img src="${url.replace(/"/g, "&quot;")}" alt="" />`);
      return null;
    }
    if (k === "image") {
      keepSel();
      return file.current && file.current.click();
    }
    if (k === "table") {
      const cell = "<td>&nbsp;</td>";
      const row = `<tr>${cell.repeat(3)}</tr>`;
      return insertHtml(`<table><thead><tr><th>Heading</th><th>Heading</th><th>Heading</th></tr></thead><tbody>${row}${row}</tbody></table><p><br></p>`);
    }
    return exec(k);
  };

  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const data = await prepareImage(f);
      const r = await api("upload", { data, folder: "blogs" });
      insertHtml(`<img src="${absUrl(r.url)}" alt="" />`);
      toast("Image जुड़ गई");
    } catch (e) {
      toast(e.message, "error");
    }
    setBusy(false);
  };

  const words = strip(value).split(" ").filter(Boolean).length;

  return (
    <div className="rounded-2xl border wa-line" style={{ background: "var(--wa-card)" }}>
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b wa-line sticky top-16 rounded-t-2xl" style={{ background: "var(--wa-card)", zIndex: 5 }}>
        {TOOLS.map((t, i) =>
          t === "|" ? (
            <span key={i} className="w-px h-5 mx-1" style={{ background: "var(--wa-line)" }} />
          ) : (
            <button key={t[0]} type="button" title={t[2]} aria-label={t[2]} disabled={html} onMouseDown={(e) => { e.preventDefault(); keepSel(); }} onClick={() => tool(t[0])}
              className="h-8 min-w-[32px] px-1.5 rounded-lg text-[13px] font-bold hover:bg-slate-500/10 disabled:opacity-30">
              {t[1].startsWith("fa-") ? <i className={t[1]} /> : t[1]}
            </button>
          )
        )}
        {busy && <i className="fa-solid fa-spinner fa-spin wa-muted mx-2" />}
        <span className="ml-auto flex items-center gap-2 pr-1">
          <span className="text-[11px] wa-muted tabular-nums hidden sm:inline">{num(words)} शब्द</span>
          <button type="button" onClick={() => setHtml((v) => !v)} className={cx("h-8 px-2.5 rounded-lg text-[12px] font-bold border wa-line", html && "text-white")} style={html ? { background: "var(--wa-accent)", borderColor: "var(--wa-accent)" } : {}}>
            <i className="fa-solid fa-code mr-1" />HTML
          </button>
        </span>
      </div>
      {html ? (
        <textarea className="w-full p-4 font-mono text-[13px] outline-none rounded-b-2xl" style={{ minHeight, background: "var(--wa-card)", color: "var(--wa-ink)" }} value={value || ""} onChange={(e) => onChange(e.target.value)} spellCheck={false} aria-label="HTML" />
      ) : (
        <div ref={ref} className="wa-editor" style={{ minHeight }} contentEditable suppressContentEditableWarning data-ph={placeholder} onInput={emit} onBlur={() => { keepSel(); emit(); }} onKeyUp={keepSel} onMouseUp={keepSel} role="textbox" aria-multiline="true" aria-label="Content" />
      )}
      <input ref={file} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
    </div>
  );
}
